// src/pages/Orders.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { getOrderList } from "../redux/actions/placeOrderAction";
import product4 from "../assets/products/product4.png";

function Orders() {
  const dispatch = useDispatch();
  const navigate = useNavigate(); // ← added
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchFilter, setSearchFilter] = useState("all");
  const filter = searchParams.get("filter") || "all";
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { orderList = [] } = useSelector((state) => state.placeOrderReducer);

  useEffect(() => {
    if (searchParams.get("filter")) {
      dispatch(getOrderList(searchParams.get("filter")));
    } else {
      dispatch(getOrderList());
    }
  }, [dispatch]);

  const formattedDate = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

  return (
    <div className="min-h-screen  bg-white flex flex-col items-center justify-between p-4">
      {/* Header Section */}
      <div className="w-full flex justify-between items-center mb-10 max-w-6xl">
        <h2 className="text-2xl font-semibold">
          {filter === "all" && "All Orders"}
          {filter === "Pending" && "New Orders"}
          {filter === "Shipped" && "In-Transit"}
          {filter === "Accepted" && "Accepted Orders"}
          {filter === "Completed" && "Delivered Orders"}
          {filter === "Returned" && "Returned Orders"}
          {filter === "Cancelled" && "Cancelled Orders"}
        </h2>
        <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenu.Trigger asChild>
            <button className="px-3 py-2 flex items-center gap-2 text-sm font-semibold text-red-500 border-[1px] border-red-500 rounded hover:text-red-600">
              {/* …icon… */}
              <span>Filter</span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="bg-[#fdf5f5] z-50 border border-neutral-200 animate-fade-in rounded-md p-4 shadow-md w-48 mt-2">
            <RadioGroup.Root
              className="flex flex-col gap-2"
              value={filter}
              onValueChange={(value) => {
                setSearchFilter(value);
                setSearchParams({ filter: value });
                if (value === "all") dispatch(getOrderList());
                else dispatch(getOrderList({ status: value }));
                setDropdownOpen(false);
              }}
            >
              {[
                "all",
                "Pending",
                "Accepted",
                "Shipped",
                "Completed",
                "Exchanged",
                "Cancelled",
              ].map((val) => (
                <label
                  key={val}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <RadioGroup.Item
                    value={val}
                    className="w-4 h-4 rounded-full border-[1px] border-gray-400 flex items-center justify-center data-[state=checked]:bg-red-500"
                  >
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </RadioGroup.Item>
                  <span>
                    {val === "all"
                      ? "All"
                      : val === "Accepted"
                      ? "On the Way"
                      : val === "Shipped"
                      ? "In-Transit"
                      : val === "Completed"
                      ? "Delivered"
                      : val}
                  </span>
                </label>
              ))}
            </RadioGroup.Root>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>

      {/* Cards Section */}
      <div className="md:bg-zinc-100/95 min-h-[90vh] rounded-2xl md:shadow-md w-full max-w-[75rem]  md:p-10 flex flex-col items-center">
        {orderList.length > 0 ? (
          orderList.map((item) => {
            // ⚡️ pull out the first ordered product

            if (!item.products || item.products.length === 0) {
              return (
                <div key={item.order_id} className="w-full max-w-6xl">
                  <div className="border mb-4 rounded-[20px] p-6 bg-white shadow-md relative flex items-center gap-4">
                    <svg
                      className="w-8 h-8 text-yellow-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M12 18h.01M21 12A9 9 0 113 12a9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h2 className="text-lg font-medium text-gray-700">
                        Product information not available
                      </h2>
                      <p className="text-sm text-gray-500">
                        We're unable to display details for this order right
                        now.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }

            const orderedProduct = item.ordered_products?.[0] || {};
            const productDetails = orderedProduct.product_details || {};

            return (
              <div key={item.order_id} className="w-full max-w-6xl">
                <div
                  className="border mb-4 rounded-[20px] p-6 bg-white shadow-md relative flex flex-col gap-3 cursor-pointer"
                  onClick={() =>
                    navigate(`/orders/track/${item.order_id}`, {
                      state: { orderData: item }, // ✅ pass full object safely
                    })
                  } // ← navigation
                >
                  {/* Status & Date */}
                  <div className="text-sm text-gray-800 space-y-1">
                    <div className="flex items-center gap-2">
                      {/* …icon… */}
                      <div className="flex flex-col leading-tight">
                        <span className="text-green-700 text-base font-medium">
                          {item.order_status === "Pending"
                            ? "New Order"
                            : item.order_status === "Accepted"
                            ? "On The Way"
                            : item.order_status === "Shipped"
                            ? "In-Transit"
                            : item.order_status === "Completed"
                            ? "Delivered"
                            : item.order_status === "Returned"
                            ? "Returned"
                            : item.order_status === "Exchanged"
                            ? "Exchanged"
                            : "Cancelled"}
                        </span>
                        <span className="text-sm text-zinc-500">
                          On {formattedDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Product Card */}
                  <div className="flex overflow-hidden items-center bg-gray-200 justify-between border border-blue-400 p-4 rounded w-full max-w-6xl">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* <img loading='lazy'
                                                src={subDetails.sub_pro_img ?? product4}
                                                alt={productDetails.product_name}
                                                className="w-20 h-24 object-cover rounded"
                                            /> */}

                      {item?.products.length > 0 && (
                        <img
                          loading="lazy"
                          src={
                            item?.products[0]?.product_primary_img ?? product4
                          }
                          alt={
                            item?.products[0]?.product_name ?? "Product Image"
                          }
                          className="w-20 h-24 object-cover rounded"
                        />
                      )}
                      <div className="break-words">
                        <h2 className="w-full overflow-hidden text-ellipsis whitespace-nowrap">
                          {item?.order_no}
                        </h2>
                        <p className="font-semibold">
                          {productDetails.product_name}
                        </p>
                        <p className="text-gray-500 font-semibold mt-1 text-sm">
                          Payment Type: {item?.payment_type}
                        </p>
                        <p className="text-black font-semibold mt-1">
                          {item.amount} ₹
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* arrow stays for UX */}
                      <svg
                        width="24"
                        height="24"
                        fill="currentColor"
                        className="text-gray-600"
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="w-full max-w-6xl mx-auto">
            <div className="border mb-6 rounded-[20px] p-8 bg-white shadow-md flex flex-col items-center justify-center gap-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856C18.07 18.664 19 16.469 19 14a7 7 0 10-14 0c0 2.469.93 4.664 2.062 6z"
                />
              </svg>
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-700">
                  No Orders Found
                </h2>
                <p className="text-sm text-gray-500">
                  We couldn’t find any orders that match your selection.
                </p>
              </div>
              {/* Optional: Add a Retry button or helpful link */}
              {/* <button
                                    onClick={handleRetry}
                                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                                    >
                                    Retry
                                    </button> 
                                */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Orders;
